(function () {
  const SELECTOR = '.image-magnify-hover';
  const OVERLAY_CLASS = 'image-magnify-full-size';
  const INITIALIZED_ATTR = 'data-magnify-hover-initialized';
  const DEFAULT_ZOOM_RATIO = 2;

  /** @type {WeakMap<Element, AbortController>} */
  const listenerControllers = new WeakMap();

  function getImageUrl(image) {
    return image.currentSrc || image.src;
  }

  function getZoomRatio(image, fallbackZoomRatio) {
    const customZoom = Number(image.dataset.zoomRatio);

    if (!Number.isNaN(customZoom) && customZoom > 1) {
      return customZoom;
    }

    return fallbackZoomRatio;
  }

  function ensureImageWrapper(image) {
    const wrapper = image.parentElement;

    if (!wrapper) return null;

    const computedStyle = window.getComputedStyle(wrapper);

    if (computedStyle.position === 'static') {
      wrapper.style.position = 'relative';
    }

    wrapper.style.overflow = 'hidden';

    return wrapper;
  }

  function createOverlay(image) {
    const wrapper = ensureImageWrapper(image);

    if (!wrapper) return null;

    const existingOverlay = wrapper.querySelector(`.${OVERLAY_CLASS}`);

    if (existingOverlay) {
      existingOverlay.remove();
    }

    const overlay = document.createElement('div');
    const imageUrl = getImageUrl(image);

    overlay.className = OVERLAY_CLASS;
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.backgroundImage = `url("${imageUrl}")`;

    wrapper.appendChild(overlay);

    return overlay;
  }

  function removeOverlay(image) {
    const wrapper = image.parentElement;

    if (!wrapper) return;

    const overlay = wrapper.querySelector(`.${OVERLAY_CLASS}`);

    if (overlay) {
      overlay.remove();
    }
  }

  function removeAllOverlays(root = document) {
    root.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((overlay) => {
      overlay.remove();
    });
  }

  function moveOverlay(image, overlay, event, zoomRatio) {
    if (!image || !overlay) return;

    const rect = image.getBoundingClientRect();

    if (!rect.width || !rect.height) return;

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const xPercent = Math.max(0, Math.min(100, (x / rect.width) * 100));
    const yPercent = Math.max(0, Math.min(100, (y / rect.height) * 100));

    overlay.style.backgroundPosition = `${xPercent}% ${yPercent}%`;
    overlay.style.backgroundSize = `${rect.width * zoomRatio}px auto`;
  }

  function isMousePointer(event) {
    return event.pointerType === 'mouse';
  }

  function unbindImage(image) {
    const controller = listenerControllers.get(image);

    if (controller) {
      controller.abort();
      listenerControllers.delete(image);
    }

    image.removeAttribute(INITIALIZED_ATTR);
    removeOverlay(image);
  }

  /**
   * Bind hover zoom listeners to images under root.
   * @param {ParentNode} [root=document]
   * @param {number} [zoomRatio=2]
   * @param {{ force?: boolean }} [options]
   */
  function enableZoomOnHover(root = document, zoomRatio = DEFAULT_ZOOM_RATIO, options = {}) {
    const scope = root && root.querySelectorAll ? root : document;
    const force = Boolean(options.force);
    const images = scope.querySelectorAll(SELECTOR);

    images.forEach((image) => {
      if (!force && image.hasAttribute(INITIALIZED_ATTR)) return;

      if (force) {
        unbindImage(image);
      }

      image.setAttribute(INITIALIZED_ATTR, 'true');

      const controller = new AbortController();
      const { signal } = controller;
      listenerControllers.set(image, controller);

      let overlay = null;

      image.addEventListener(
        'pointerenter',
        (event) => {
          /**
           * Important:
           * Only allow real mouse hover.
           * This prevents mobile/touch taps from triggering the zoom,
           * but still allows hover on touch-enabled desktop/laptop devices.
           */
          if (!isMousePointer(event)) return;

          if (!image.complete) return;

          const activeZoomRatio = getZoomRatio(image, zoomRatio);

          overlay = createOverlay(image);

          if (!overlay) return;

          moveOverlay(image, overlay, event, activeZoomRatio);
        },
        { signal }
      );

      image.addEventListener(
        'pointermove',
        (event) => {
          if (!isMousePointer(event)) return;

          if (!overlay) return;

          const activeZoomRatio = getZoomRatio(image, zoomRatio);

          moveOverlay(image, overlay, event, activeZoomRatio);
        },
        { signal }
      );

      image.addEventListener(
        'pointerleave',
        () => {
          removeOverlay(image);
          overlay = null;
        },
        { signal }
      );

      image.addEventListener(
        'pointercancel',
        () => {
          removeOverlay(image);
          overlay = null;
        },
        { signal }
      );

      image.addEventListener(
        'pointerdown',
        (event) => {
          /**
           * If user touches/taps the image, make sure no zoom overlay stays active.
           */
          if (event.pointerType === 'touch') {
            removeOverlay(image);
            overlay = null;
          }
        },
        { signal }
      );

      image.addEventListener(
        'load',
        () => {
          removeOverlay(image);
          overlay = null;
        },
        { signal }
      );
    });

    return images.length;
  }

  function initMagnifyZoom() {
    enableZoomOnHover(document, DEFAULT_ZOOM_RATIO);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMagnifyZoom);
  } else {
    initMagnifyZoom();
  }

  /**
   * Shopify Theme Editor support.
   * Re-initialize when a section reloads.
   */
  document.addEventListener('shopify:section:load', (event) => {
    enableZoomOnHover(event.target, DEFAULT_ZOOM_RATIO, { force: true });
  });

  /**
   * Horizon replaces <media-gallery> on variant change — rebind after DOM swap.
   */
  document.addEventListener('variant:update', () => {
    requestAnimationFrame(() => {
      const gallery = document.querySelector('media-gallery[data-zoom-on-hover]');
      if (!gallery) return;
      enableZoomOnHover(gallery, DEFAULT_ZOOM_RATIO, { force: true });
    });
  });

  /**
   * Remove overlays during scroll to avoid stuck overlays on hybrid devices.
   */
  window.addEventListener(
    'scroll',
    () => {
      removeAllOverlays(document);
    },
    { passive: true }
  );

  // media-gallery.js owns window.reinitProductZoom (always loaded).
  // Expose the binder so that helper can force-rebind hover zoom.
  window.enableZoomOnHover = enableZoomOnHover;
})();
